import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { formatApiErrorDetail } from '../lib/api';

const ART = 'https://lh3.googleusercontent.com/aida-public/AB6AXuDp6aXE1NM4HErOsVemu6-hKdyMjPXeb4bSprNg2bnzv1ftLZhDnoLWg00zBlp_l2yy8CrMN36m92jn7CWgyNUk3tm2a2NFoPdNre1qSvaSsRjdz0f3jOJQqHqnDE_xiypiCf6F7eIGWXbUYl58ljXQ0FrsH9DUKI6PsaqSTvvi3DKyxIo4Cn8cxUVJ4y2EDkihfSlyrP4BD9UMgYfMG3NmH0Ok94TXVRI96ig2bgz3-V2pgKhPky0';

const inputCls =
  'w-full h-12 px-space-md bg-surface-container-low rounded-lg border border-surface-container font-body-md text-body-md text-outline focus:outline-none focus:border-surface-tint focus:ring-1 focus:ring-surface-tint transition-all placeholder:text-on-surface-variant/60';
const labelCls =
  'font-label-md text-label-md uppercase tracking-wider text-outline mb-space-xs font-semibold block';

export default function AuthScreen() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login'); // login | register | forgot | reset
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('reset');
    if (t) {
      setResetToken(t);
      setMode('reset');
    }
  }, []);

  const reset = () => {
    setError('');
    setNotice('');
  };

  const clearResetParam = () => {
    window.history.replaceState({}, '', '/my-login');
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH.
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + '/my-login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {    e.preventDefault();
    reset();
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(email.trim(), password);
      } else if (mode === 'register') {
        await register(name.trim(), email.trim(), password);
      } else if (mode === 'reset') {
        await api.post('/auth/reset-password', { token: resetToken, password });
        clearResetParam();
        setNotice('Your password has been updated. You can now sign in.');
        setMode('login');
        setPassword('');
      } else {
        const { data } = await api.post('/auth/forgot-password', { email: email.trim() });
        setNotice(data.message || 'If that email exists, a reset link has been sent.');
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full px-margin-mobile lg:px-margin py-space-2xl" data-testid="auth-screen">
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 rounded-xl overflow-hidden border border-surface-container shadow-sm">
        {/* Brand panel */}
        <div className="lg:col-span-5 bg-outline text-on-primary p-space-xl flex flex-col justify-between relative overflow-hidden">
          <div className="absolute top-0 left-0 bottom-0 w-2 bg-surface-tint shadow-[0_0_16px_rgba(240,40,223,0.5)]" />
          <div className="relative z-10">
            <span className="font-label-md text-label-md uppercase tracking-widest text-primary-fixed font-semibold">
              Your Private Space
            </span>
            <h2 className="font-headline-lg text-headline-lg uppercase tracking-tight mt-space-sm leading-snug">
              Your Calm <br />
              <span className="text-surface-tint">Anchor</span>
            </h2>
            <p className="font-body-md text-body-md text-surface-container-highest mt-space-md max-w-sm leading-relaxed">
              Sign in to focus on what matters right now — evidence-led guidance,
              gentle habits, and reflections that stay private to you.
            </p>
          </div>
          <div
            className="hidden lg:block mt-space-xl h-44 rounded-lg bg-cover bg-center relative z-10"
            style={{ backgroundImage: `url('${ART}')` }}
          />
        </div>

        {/* Form panel */}
        <div className="lg:col-span-7 bg-surface-container-lowest p-space-xl">
          <div className="flex items-center gap-space-sm mb-space-lg">
            <span className="inline-block w-2 h-2 rounded-full bg-surface-tint shadow-[0_0_10px_rgba(240,40,223,0.7)]" />
            <span className="font-label-md text-label-md uppercase tracking-wider text-on-surface-variant">
              {mode === 'login' && 'Welcome Back'}
              {mode === 'register' && 'Create Your Space'}
              {mode === 'forgot' && 'Recover Access'}
              {mode === 'reset' && 'Choose a New Password'}
            </span>
          </div>

          <h1 className="font-headline-md text-headline-md tracking-tight text-outline mb-space-lg">
            {mode === 'login' && 'Sign In'}
            {mode === 'register' && 'Sign Up'}
            {mode === 'forgot' && 'Reset Password'}
            {mode === 'reset' && 'Set New Password'}
          </h1>

          {error ? (
            <div
              data-testid="auth-error"
              className="mb-space-md px-space-md py-space-sm rounded-lg bg-error-container text-on-error-container font-body-sm text-body-sm"
            >
              {error}
            </div>
          ) : null}
          {notice ? (
            <div
              data-testid="auth-notice"
              className="mb-space-md px-space-md py-space-sm rounded-lg bg-primary-container text-on-primary-fixed-variant font-body-sm text-body-sm"
            >
              {notice}
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="flex flex-col gap-space-md" data-testid="auth-form">
            {mode === 'register' && (
              <div>
                <label className={labelCls} htmlFor="auth-name">Full Name</label>
                <input
                  id="auth-name"
                  data-testid="auth-name-input"
                  className={inputCls}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Elena Rivers"
                  required
                />
              </div>
            )}

            <div>
              <label className={labelCls} htmlFor="auth-email">Email</label>
              <input
                id="auth-email"
                data-testid="auth-email-input"
                type="email"
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required={mode !== 'reset'}
                disabled={mode === 'reset'}
                style={mode === 'reset' ? { display: 'none' } : undefined}
              />
            </div>

            {mode !== 'forgot' && (
              <div>
                <label className={labelCls} htmlFor="auth-password">{mode === 'reset' ? 'New Password' : 'Password'}</label>
                <input
                  id="auth-password"
                  data-testid="auth-password-input"
                  type="password"
                  className={inputCls}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'login' ? '••••••••' : 'At least 8 characters'}
                  minLength={mode === 'login' ? undefined : 8}
                  required
                />
              </div>
            )}

            <button
              type="submit"
              data-testid="auth-submit-button"
              disabled={busy}
              className="h-12 mt-space-xs rounded-lg bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-wider font-semibold shadow-[0_4px_16px_rgba(240,40,223,0.3)] hover:bg-primary transition-all disabled:opacity-60"
            >
              {busy
                ? 'Please wait…'
                : mode === 'login'
                ? 'Sign In'
                : mode === 'register'
                ? 'Create Account'
                : mode === 'reset'
                ? 'Set New Password'
                : 'Send Reset Link'}
            </button>
          </form>

          {(mode === 'login' || mode === 'register') && (
            <>
              <div className="flex items-center gap-space-md mt-space-lg">
                <span className="h-px flex-1 bg-surface-container" />
                <span className="font-label-sm text-label-sm uppercase tracking-widest text-on-surface-variant">or</span>
                <span className="h-px flex-1 bg-surface-container" />
              </div>

              <button
                type="button"
                data-testid="auth-google-button"
                onClick={handleGoogle}
                className="w-full h-12 mt-space-lg rounded-lg bg-surface-container-lowest border border-surface-container-highest text-outline font-label-md text-label-md uppercase tracking-wider font-semibold flex items-center justify-center gap-space-sm hover:border-surface-tint hover:bg-primary-container/40 transition-all"
              >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                </svg>
                <span>Continue with Google</span>
              </button>
              <p className="font-body-sm text-[12px] text-on-surface-variant mt-space-sm leading-relaxed">
                We only ever receive your name, email address and profile picture from Google. We never see your Google password.
              </p>
            </>
          )}

          <div className="mt-space-lg pt-space-md border-t border-surface-container flex flex-col gap-space-xs font-body-sm text-body-sm text-on-surface-variant">
            {mode === 'login' && (
              <>
                <button
                  type="button"
                  data-testid="auth-switch-register"
                  className="text-left hover:text-surface-tint transition-colors"
                  onClick={() => { setMode('register'); reset(); }}
                >
                  New here? <span className="text-outline font-semibold">Create an account →</span>
                </button>
                <button
                  type="button"
                  data-testid="auth-switch-forgot"
                  className="text-left hover:text-surface-tint transition-colors"
                  onClick={() => { setMode('forgot'); reset(); }}
                >
                  Forgot your password?
                </button>
              </>
            )}
            {mode === 'register' && (
              <button
                type="button"
                data-testid="auth-switch-login"
                className="text-left hover:text-surface-tint transition-colors"
                onClick={() => { setMode('login'); reset(); }}
              >
                Already have an account? <span className="text-outline font-semibold">Sign in →</span>
              </button>
            )}
            {mode === 'forgot' && (
              <button
                type="button"
                data-testid="auth-switch-login"
                className="text-left hover:text-surface-tint transition-colors"
                onClick={() => { setMode('login'); reset(); }}
              >
                ← Back to sign in
              </button>
            )}
            {mode === 'reset' && (
              <button
                type="button"
                data-testid="auth-switch-login"
                className="text-left hover:text-surface-tint transition-colors"
                onClick={() => { clearResetParam(); setMode('login'); reset(); }}
              >
                ← Back to sign in
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
